import React from 'react';

interface StatusIndicatorProps {
    dockerStatus: {
        running: number;
        total: number;
    };
    diskStatus: {
        critical: number;
        warning: number;
        total: number;
    };
}

const styles = {
    container: {
        backgroundColor: 'var(--metro-card-bg)',
        border: '1px solid var(--metro-card-border)',
        borderRadius: 'var(--card-radius)',
        padding: '20px',
        marginBottom: '20px',
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '15px'
    },
    title: {
        margin: 0,
        color: 'var(--metro-text)',
        fontSize: '18px',
        fontWeight: 500
    },
    statusGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '20px'
    },
    statusCard: {
        backgroundColor: 'var(--metro-dark-secondary)',
        padding: '15px',
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '10px'
    },
    statusTitle: {
        color: 'var(--metro-text-secondary)',
        fontSize: '14px',
        margin: 0
    },
    statusValue: {
        color: 'var(--metro-text)',
        fontSize: '24px',
        fontWeight: 600,
        margin: 0,
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
    },
    indicator: {
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        display: 'inline-block'
    },
    green: {
        backgroundColor: '#4CAF50'
    },
    yellow: {
        backgroundColor: '#FFC107'
    },
    red: {
        backgroundColor: '#F44336'
    }
};

const StatusIndicator: React.FC<StatusIndicatorProps> = ({ dockerStatus, diskStatus }) => {
    const getDockerStatusColor = () => {
        const ratio = dockerStatus.running / dockerStatus.total;
        if (ratio === 1) return styles.green;
        if (ratio >= 0.8) return styles.yellow;
        return styles.red;
    };

    const getDiskStatusColor = () => {
        const criticalRatio = diskStatus.critical / diskStatus.total;
        const warningRatio = diskStatus.warning / diskStatus.total;
        
        if (criticalRatio > 0) return styles.red;
        if (warningRatio > 0) return styles.yellow;
        return styles.green;
    };

    return (
        <div style={styles.container}>
            <h3 style={styles.title}>System Status Overview</h3>
            <div style={styles.statusGrid}>
                <div style={styles.statusCard}>
                    <h4 style={styles.statusTitle}>Docker Containers</h4>
                    <div style={styles.statusValue}>
                        <span style={{...styles.indicator, ...getDockerStatusColor()}}></span>
                        {dockerStatus.running} / {dockerStatus.total} Running
                    </div>
                </div>
                <div style={styles.statusCard}>
                    <h4 style={styles.statusTitle}>Disk Space</h4>
                    <div style={styles.statusValue}>
                        <span style={{...styles.indicator, ...getDiskStatusColor()}}></span>
                        {diskStatus.critical} Critical, {diskStatus.warning} Warning
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StatusIndicator; 